import Add from '@mui/icons-material/Add'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import { Alert, Box, Button, List, ListItem, Stack, Typography } from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { Answer } from '../api/generated/Answer'
import type { Question } from '../api/generated/Question'
import type { QuestionFields } from '../api/generated/QuestionFields'
import type { QuestionOrder } from '../api/generated/QuestionOrder'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { ShiftAction } from '../shifts/ShiftList'
import { kindLabels, moved } from './form'
import { QuestionDialog } from './QuestionDialog'
import { summarize } from './summary'

type Dialog = { kind: 'new' } | { kind: 'edit'; question: Question }

function Summary({ question, answers }: { question: Question; answers: readonly Answer[] }) {
  const { respondents, counts } = summarize(question, answers)
  if (question.kind === 'checkbox')
    return (
      <Typography variant="body2">
        {respondents} {respondents > 1 ? 'cochées' : 'cochée'}
      </Typography>
    )
  return (
    <>
      <Typography variant="body2">
        {respondents} {respondents > 1 ? 'réponses' : 'réponse'}
      </Typography>
      {question.kind !== 'long_text' && (
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          {counts.map(({ value, count }) => (
            <Typography component="li" variant="body2" key={value}>
              {value} : {count}
            </Typography>
          ))}
        </Box>
      )}
    </>
  )
}

/**
 * An organizer's questions to applicants, in the order they are asked, with how volunteers
 * answered them; each volunteer's answers are shown with their applications.
 */
export function QuestionList({ slug }: { slug: string }) {
  const path = `/events/${slug}/questions`
  const questions = useApi<Question[]>(path)
  const answers = useApi<Answer[]>(`/events/${slug}/answers`)
  const [dialog, setDialog] = useState<Dialog>()
  const action = useAction()

  const saved = (list: Question[]) => {
    questions.setData(list)
    setDialog(undefined)
  }
  const create = async (fields: QuestionFields) => saved(await api.post<Question[]>(path, fields))
  const update = (question: Question) => async (fields: QuestionFields) => {
    saved(await api.patch<Question[]>(`${path}/${question.id}`, fields))
    // A new kind may have deleted its answers
    answers.reload()
  }
  const remove = (question: Question) => {
    if (!confirm(`Supprimer la question « ${question.label} » et ses réponses ?`)) return
    action.run(async () => {
      questions.setData(await api.delete<Question[]>(`${path}/${question.id}`))
      answers.setData((current) => current?.filter((a) => a.question_id !== question.id))
    })
  }
  const move = (list: Question[], from: number, to: number) =>
    action.run(async () => {
      const body: QuestionOrder = {
        question_ids: moved(
          list.map((q) => q.id),
          from,
          to,
        ),
      }
      questions.setData(await api.post<Question[]>(`${path}/order`, body))
    })
  const list = questions.data

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Questions aux bénévoles</Typography>
        <Button startIcon={<Add />} disabled={!list} onClick={() => setDialog({ kind: 'new' })}>
          Ajouter
        </Button>
      </Stack>
      {questions.error && <Alert severity="error">{questions.error}</Alert>}
      {answers.error && <Alert severity="error">{answers.error}</Alert>}
      {action.error && <Alert severity="error">{action.error}</Alert>}
      {list?.length === 0 && (
        <Typography color="text.secondary">
          Posées aux bénévoles quand ils s'inscrivent : taille de t-shirt, régime alimentaire,
          commentaire…
        </Typography>
      )}
      {list && list.length > 0 && (
        <List dense disablePadding>
          {list.map((question, index) => (
            <ListItem
              key={question.id}
              disableGutters
              divider
              sx={{ flexDirection: 'column', alignItems: 'stretch' }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography sx={{ fontWeight: 'medium' }}>{question.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kindLabels[question.kind]}
                    {question.required && ', obligatoire'}
                  </Typography>
                </Box>
                <Box>
                  <ShiftAction
                    label="Monter"
                    disabled={index === 0 || action.pending}
                    onClick={() => move(list, index, index - 1)}
                  >
                    <ArrowUpward fontSize="small" />
                  </ShiftAction>
                  <ShiftAction
                    label="Descendre"
                    disabled={index === list.length - 1 || action.pending}
                    onClick={() => move(list, index, index + 1)}
                  >
                    <ArrowDownward fontSize="small" />
                  </ShiftAction>
                  <ShiftAction
                    label="Modifier"
                    onClick={() => setDialog({ kind: 'edit', question })}
                  >
                    <Edit fontSize="small" />
                  </ShiftAction>
                  <ShiftAction
                    label="Supprimer"
                    disabled={action.pending}
                    onClick={() => remove(question)}
                  >
                    <Delete fontSize="small" />
                  </ShiftAction>
                </Box>
              </Stack>
              {answers.data && <Summary question={question} answers={answers.data} />}
            </ListItem>
          ))}
        </List>
      )}
      {dialog?.kind === 'new' && (
        <QuestionDialog
          title="Nouvelle question"
          onSubmit={create}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog?.kind === 'edit' && (
        <QuestionDialog
          title="Modifier la question"
          initial={dialog.question}
          onSubmit={update(dialog.question)}
          onClose={() => setDialog(undefined)}
        />
      )}
    </Stack>
  )
}
